import React, {useState, useEffect, useRef} from 'react'
import Slider from 'react-slick'
import { Link } from 'react-router-dom'
import { isEmpty } from 'lodash'
import classNames from 'classnames'

import Button from '@mui/material/Button'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ChevronLeft from '@mui/icons-material/ChevronLeft'

import { Icons } from '../../components/Icons'
import iconPlay from '../../assets/icons/play.svg'
import iconInfo from '../../assets/icons/info.svg'
import { useScrollPosition } from '../../hooks/useScrollPosition'

import { useSelector } from 'react-redux'

export default function HomeHero(props) {
  const {
    row,
    handleOnClickWatchNow,
    navigate,
    useImageCDN,
    sponsored
  } = props

  const { idUser } = useSelector(state => state.auth)
  const [heroVideoData, setHeroVideoData] = useState([])
  const firstSlideRef = useRef(null);
  const scrollPosition = useScrollPosition()

  // Función para dejar fixed header y cambiar opacidad del hero
  const normalizeBetweenTwoRanges = (val, minVal, maxVal, newMin, newMax) => {
    return newMin + (val - minVal) * (newMax - newMin) / (maxVal - minVal)
  }

  const newOpacity = Math.min(1, Math.max(0, normalizeBetweenTwoRanges(scrollPosition, 0, 200, 1, 0)))

  const SliderArrow = (props) => {
    const { className, onClick, IconPath } = props
    return (
      <div className={className + ' home-hero-arrow'} onClick={onClick}>
        <div className='icon-border'><IconPath /></div>
      </div>
    )
  }

  // Add video id to heroVideoData
  const addVideoId = (index) => {
    const video = document.getElementById(`video_homeHero_${index}`) 
    // If there is no video inside element
    if (video?.hasChildNodes() === false){
      const newVideo = loadHeroVideo(index, video)
      setHeroVideoData(prevData => [
        ...prevData,
        {id: index, value: newVideo},
      ]);
    }
  }
  
  // Play current video
  const playVideoByIndex = (index) => {
    const currVideo = heroVideoData.find((video) => video.id === index)
    if (!isEmpty(currVideo)){
      currVideo.value.videoPlay()
    }
  }

  // Pause current video
  const pauseVideoByIndex = (index) => {
    const currVideo = heroVideoData.find((video) => video.id === index)
    if (!isEmpty(currVideo)){
      currVideo.value.videoStop()
    }
  }

  // Base options for mds player
  const playerOptions = {
    width: 640,
    height: 360,
    type: 'media',
    id: '',
    autoplay: true,
    player: process.env.REACT_APP_MDSTRM_ID_PLAYER,
    controls: false,
    show_controls_on_ad: false,
    pause_on_screen_click: false,
    pause_ad_on_click: false,
    customer: idUser,
    volume: 0,
    playbackLevel: 3,
    events: {
      onPlayerReady: function () {
        this.playbackLevel(3)
      },
      onPlay: function () {
        this._playerContainer.classList.add("video-ready")
        // this.playbackLevels(levels => console.log(levels))
      },
      onVideoEnd: function() {
        this.seekTo(0)
        this.videoPlay()
      },
    },
  }

  // Load video by index
  const loadHeroVideo = (index, video) => {
    const trailerId = row.content[index].vodTrailer?.idMedia
    playerOptions.id = trailerId
    if (!isEmpty(trailerId)){
      return new window.MediastreamPlayer(`video_homeHero_${index}`, playerOptions)
    } else {
      const emptyDiv = document.createElement('div');
      video.appendChild(emptyDiv)
    }
  }

  // On render, load the first slider video
  useEffect(() => {
    if (firstSlideRef.current) {
      addVideoId(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstSlideRef.current]);
  
  // Slider settings
  const settings = {
    initialSlide: 0,
    fade: true,
    dots: row?.content?.length > 1,
    infinite: row?.content?.length > 1,
    speed: 800,
    autoplay: true,
    autoplaySpeed: 7000,
    pauseOnHover: true,
    pauseOnDotsHover: true,
    waitForAnimate: true,
    lazyLoad: 'ondemand',
    appendDots: (dots) => (
      <ul className='dotsList'>
        {
          dots.map(dot => {
            const { className: dotClassName, children: buttonDot } = dot.props
            const { onClick: fnDot } = buttonDot.props
            return (
              <li key={dot.key} className={`hero-dot ${dotClassName === 'slick-active' ? 'hero-dot-active' : ''}`}>
                <button onClick={fnDot}><div></div></button>
              </li>
            )
          })
        }
      </ul>
    ),
    nextArrow: <SliderArrow IconPath={ChevronRight} />,
    prevArrow: <SliderArrow IconPath={ChevronLeft} />,
    afterChange: (newIndex) => {
      // Create next video
      addVideoId(newIndex)
      // Play next video
      playVideoByIndex(newIndex)
    },
    beforeChange: (oldIndex) => {
      // Pause current video
      pauseVideoByIndex(oldIndex)
    },
  }

  const HeroSlides = () => (
    row.content.map((item, index) => (
      <div key={item._id}>
        <article
          style={item._id === sponsored._id ? { backgroundColor: sponsored.bgColor } : {}}
          className={classNames({
            'sponsoredItem': item._id === sponsored._id
          })}
        >
          <div className='itemContainer'>
            <div className='heroHeader'>
              <div className='mainWidth'>
                {item.type === 'Live' &&
                  <span className='itemTag'>En vivo</span>
                }
                <figure className='heroFig'>
                  <img
                    src={!isEmpty(item.imagesSite.logo)
                      ? useImageCDN
                        ? item.imagesSite.logo.replace('https://api-megago.megamedia.cl', 'https://megago.cdn.mdstrm.com')
                        : item.imagesSite.logo
                      : 'https://images2-mega.cdn.mdstrm.com/ott/placeholders-imgs/placeholder-logo.png'
                    }
                    alt={item.title}
                  />
                </figure>
                <Button
                  variant='contained'
                  startIcon={<Icons path={iconPlay} />}
                  onClick={item.type === 'Serie'
                    ? () => handleOnClickWatchNow(item || {})
                    : () => navigate(`/player/live/${item._id}`)
                  }
                >
                  Ver ahora
                </Button>
                {item.type === 'Serie' && <Link to={`/serie/${item._id}`}>
                  <Button variant='contained' startIcon={<Icons path={iconInfo} />}>Ver más</Button>
                </Link>}
                {item._id === sponsored._id &&
                  <a href={sponsored.url} target="_blank" className='sponsoredLink' rel="noreferrer">
                    <span>Patrocinado por:</span>
                    <img
                      src={sponsored.imageUrl}
                      alt='placeholder'
                    />
                  </a>
                }
              </div>
            </div>
            <div className='heroImgBox'>
              <span className='shadowTop'></span>
              <span className='shadowRight'></span>
              <span className='shadowLeft'></span>
              <span className='shadowBottom'></span>
              <figure className='ratioImgBox ratioHero ratioVideo'>
                <img
                  src={!isEmpty(item.imagesSite.hero)
                    ? useImageCDN
                      ? item.imagesSite.hero.replace('https://api-megago.megamedia.cl', 'https://megago.cdn.mdstrm.com')
                      : item.imagesSite.hero
                    : 'https://images2-mega.cdn.mdstrm.com/ott/placeholders-imgs/placeholder-hero.jpg'
                  }
                  alt={item?.title || ''}
                />
                {item?.vodTrailer?.idMedia &&
                  <div id={`video_homeHero_${index}`} ref={index === 0 ? firstSlideRef : null}></div>
                }
              </figure>
            </div>
          </div>
          <span className='shadowBottom'></span>
        </article>
      </div>
    ))
  )

  return (
    <div className='heroContentWrap' style={{ opacity: newOpacity }}>
      <Slider
        className='newsSliderHighlighted'
        {...settings}
      >
        {HeroSlides()}
      </Slider>
    </div>
  )
}